
/* ============================== src/components/Modals/RolesModal.jsx ============================== */
/**
 * Модалка управления ролями и привязками к КБ.
 * Позволяет назначать роли, выбирать КБ для главного конструктора.
 * При сохранении отправляет данные без ручного приведения типов.
 */
import { useCallback, useState } from "react";
import { ModalShell } from "../ModalShell";
import { useAsyncSubmit } from "../../hooks/useAsyncSubmit";
import { ROLES } from "../../utils/constants";
import { ColoredSpan } from "../ColoredSpan";
import {
  useEmployeeQuery,
  useUpdateEmployeeMutation,
} from "../../api/queries/employees";
import { useKbsQuery } from "../../api/queries/kbs";

export const RolesModal = ({ empId, onClose, toast }) => {
  const employeeQ = useEmployeeQuery(empId);
  const emp = employeeQ.data;

  if (!emp) {
    return (
      <ModalShell
        title="Роли"
        onClose={onClose}
        width={520}
        className="modal-roles"
        showSave={false}
        footer={
          <div className="modal-foot">
            <div className="spacer" />
            <button type="button" className="btn ghost" onClick={onClose}>
              Закрыть
            </button>
          </div>
        }
      >
        <div className="empty-note p-4">
          {employeeQ.isError
            ? "Не удалось загрузить данные сотрудника"
            : "Загрузка…"}
        </div>
      </ModalShell>
    );
  }

  return (
    <RolesForm
      key={emp.id}
      emp={emp}
      empId={empId}
      onClose={onClose}
      toast={toast}
    />
  );
};

const RolesForm = ({ emp, empId, onClose, toast }) => {
  const kbsQ = useKbsQuery();
  const updateMutation = useUpdateEmployeeMutation();
  const kbs = kbsQ.data ?? [];

  const [roles, setRoles] = useState(() => emp.roles || []);
  const [kbIds, setKbIds] = useState(() => emp.kbIds || []);

  const toggleRole = (role) => {
    setRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role],
    );
  };

  const toggleKb = (kbId) => {
    setKbIds((prev) =>
      prev.includes(kbId) ? prev.filter((id) => id !== kbId) : [...prev, kbId],
    );
  };

  const { submit: save, isSubmitting } = useAsyncSubmit(
    useCallback(async () => {
      if (roles.includes("kb_chief") && kbIds.length === 0) {
        throw new Error(
          'Для роли "Главный конструктор" необходимо выбрать хотя бы одно КБ',
        );
      }

      const nextDepartments = roles.includes("kb_chief")
        ? []
        : emp.departments;

      await updateMutation.mutateAsync({
        id: empId,
        body: {
          roles,
          kbIds: roles.includes("kb_chief") ? kbIds : [],
          departments: nextDepartments,
        },
      });
      onClose();
    }, [emp, roles, kbIds, updateMutation, empId, onClose]),
    (error) => {
      toast(error.message || "Ошибка сохранения ролей", "error");
    },
  );

  return (
    <ModalShell
      title={`Роли - ${emp.last} ${emp.first}`}
      onClose={onClose}
      onSave={save}
      width={520}
      className="modal-roles"
      saveDisabled={isSubmitting}
    >
      <p className="mut sm">
        Сотрудник может иметь несколько ролей. Для «Главного конструктора»
        укажите КБ, в которых он работает. Основная должность и подразделения
        указываются в карточке сотрудника и в его личном кабинете. Роль
        «Исполнитель» управляется автоматически: появляется при назначении
        задач, снимается - когда их не остаётся (если есть другие роли).
      </p>

      <div className="roles-list">
        {Object.entries(ROLES).map(([k, v]) => {
          const isDerived = k === "executor";
          return (
            <div key={k}>
              <label className="roles-item">
                <input
                  type="checkbox"
                  checked={roles.includes(k)}
                  onChange={() => toggleRole(k)}
                  disabled={isDerived}
                />
                <ColoredSpan
                  color={v.color}
                  className="role-chip role-chip--tinted"
                >
                  {v.short}
                </ColoredSpan>
                {v.label}
                {isDerived && (
                  <span className="mut sm ml-auto">
                    назначается автоматически
                  </span>
                )}
              </label>

              {k === "kb_chief" && roles.includes("kb_chief") && (
                <div className="sub-picks">
                  {kbs.map((kb) => (
                    <label className="dept-pick" key={kb.id}>
                      <input
                        type="checkbox"
                        checked={kbIds.includes(kb.id)}
                        onChange={() => toggleKb(kb.id)}
                      />
                      {kb.name}
                    </label>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </ModalShell>
  );
};
